import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Collapse } from "react-bootstrap";
import Select from "react-select";
import { useParams, useSearchParams } from "react-router-dom";
import { MdKeyboardArrowDown, MdClose, MdContentCopy, MdDeleteOutline, MdEdit, MdOutlineCheckCircle, MdOutlineRemoveCircleOutline, MdAddCircle, MdFileDownload } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { SUPPORT_FIELD_TYPE_OPTIONS, Utils, fetchWithAuth, selectStyles, copyToClipboard, getStatusDisplay, handleSessionExpired, getDefaultPageSize, DEFAULT_STATUS_OPTIONS, can, getAvatarInitials, nsAvatarColor, invalidateSupportFieldsCache, exportListCsv } from "../../utils";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";

const DEFAULT_FIELD_TYPE_OPTIONS = Array.isArray(SUPPORT_FIELD_TYPE_OPTIONS) && SUPPORT_FIELD_TYPE_OPTIONS.length
  ? SUPPORT_FIELD_TYPE_OPTIONS
  : [{ value: "text", label: "Text" }];
const FIELD_TYPES_WITH_OPTIONS = new Set(["dropdown", "multi_select"]);

const normalizeOptionRows = (input) => {
  if (!Array.isArray(input)) return [];
  return input.map((item) => {
    if (item && typeof item === "object" && !Array.isArray(item)) {
      return {
        label: String(item.label || "").trim(),
        value: String(item.value || "").trim(),
      };
    }
    const str = String(item || "").trim();
    return { label: str, value: str };
  });
};

const parseOptionsJson = (raw) => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return normalizeOptionRows(parsed);
  } catch (e) {
    return [];
  }
};

const isStandardField = (row) => String(row?.field_id || "").toLowerCase().startsWith("std_");

const AppSupportFields = () => {
  const { appId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getDefaultPageSize();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ field_id: "", name: "", field_type: "", status: "" });
  const [draftFilters, setDraftFilters] = useState({ field_id: "", name: "", field_type: "", status: "" });
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const [fieldTypeOptions, setFieldTypeOptions] = useState(DEFAULT_FIELD_TYPE_OPTIONS);
  const [isViewMode, setIsViewMode] = useState(false);
  const [form, setForm] = useState({
    id: null,
    field_id: "",
    name: "",
    field_type: "text",
    options_list: [],
    is_required: 0,
    status: 1,
  });
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);
  const getFieldActionOptionCount = (item) => {
    let n = 1; // Copy Field ID
    if (isStandardField(item)) {
      n += 1; // View
      if (can("support.fields.change_status")) n += 1;
    } else {
      if (can("support.fields.edit")) n += 1;
      if (can("support.fields.change_status")) n += 1;
      if (can("support.fields.delete")) n += 1;
    }
    return n;
  };
  const { openActionId, openActionItem, dropdownPosition, dropdownRef, handleDropdownAction, handleOpenDropdown } = useActionDropdown({
    tableScrollRef,
    optionCount: getFieldActionOptionCount,
    menuWidth: 220,
    useAnchored: true,
  });

  const loadData = async (nextPage = page, nextFilters = filters) => {
    setLoading(true);
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/fields`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        page: nextPage,
        pageSize,
        field_id: (nextFilters.field_id || "").trim(),
        name: (nextFilters.name || "").trim(),
        field_type: (nextFilters.field_type || "").trim(),
        status: nextFilters.status,
      }),
    });
    const result = await res.json().catch(() => ({}));
    setLoading(false);
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Failed to load fields");
    setRows(result.data || []);
    setMeta(result.meta || { page: 1, totalPages: 1, total: 0 });
    const allowedTypes = Array.isArray(result?.meta?.fieldTypeOptions) && result.meta.fieldTypeOptions.length
      ? result.meta.fieldTypeOptions
      : DEFAULT_FIELD_TYPE_OPTIONS;
    setFieldTypeOptions(allowedTypes);
  };

  useEffect(() => {
    if (!appId) return;
    const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const field_id = searchParams.get("field_id") || "";
    const name = searchParams.get("name") || "";
    const field_type = searchParams.get("field_type") || "";
    const status = searchParams.get("status") || "";
    const f = { field_id, name, field_type, status };
    setPage(p);
    setFilters(f);
    setDraftFilters(f);
    setFilterAccordionOpen(Boolean(field_id || name || field_type || status));
    loadData(p, f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, searchParams]);

  const writeParams = (p, f) => {
    const sp = new URLSearchParams();
    if (p > 1) sp.set("page", String(p));
    if ((f.field_id || "").trim()) sp.set("field_id", f.field_id.trim());
    if ((f.name || "").trim()) sp.set("name", f.name.trim());
    if ((f.field_type || "").trim()) sp.set("field_type", f.field_type.trim());
    if (String(f.status || "").trim()) sp.set("status", String(f.status).trim());
    setSearchParams(sp, { replace: true });
  };

  const onApplyFilters = () => {
    const f = { ...draftFilters };
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(Boolean((f.field_id || "").trim() || (f.name || "").trim() || (f.field_type || "").trim() || (f.status || "").trim()));
    writeParams(1, f);
  };

  const onResetFilters = () => {
    const f = { field_id: "", name: "", field_type: "", status: "" };
    setDraftFilters(f);
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(false);
    writeParams(1, f);
  };


  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    const isOptionsType = FIELD_TYPES_WITH_OPTIONS.has(String(form.field_type || "").trim().toLowerCase());
    const options = Array.isArray(form.options_list)
      ? form.options_list
        .map((item) => {
          const label = String(item.label || "").trim();
          const valueRaw = String(item.value || "").trim();
          return { label, value: valueRaw || label };
        })
        .filter((item) => item.label)
      : [];
    if (isOptionsType && options.length === 0) {
      return toast.error("Please add at least one option for this field type");
    }
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/field/save`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        id: form.id,
        field_id: form.field_id,
        name: form.name,
        field_type: form.field_type,
        options: isOptionsType ? options : null,
        is_required: form.is_required ? 1 : 0,
        status: form.status ? 1 : 0,
      }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Failed to save field");
    toast.success("Field saved");
    invalidateSupportFieldsCache(appId);
    setShowModal(false);
    setForm({ id: null, field_id: "", name: "", field_type: "text", options_list: [], is_required: 0, status: 1 });
    loadData();
  };

  const openFieldInModal = (item, { viewOnly = false } = {}) => {
    setForm({
      id: item.id,
      field_id: item.field_id,
      name: item.name,
      field_type: item.field_type || "text",
      options_list: parseOptionsJson(item.options_json),
      is_required: Number(item.is_required) === 1 ? 1 : 0,
      status: Number(item.status) === 0 ? 0 : 1,
    });
    setIsViewMode(viewOnly);
    setShowModal(true);
  };

  const toggleFieldStatus = async (item) => {
    if (!item?.id) return;
    const nextStatus = Number(item.status) === 1 ? 0 : 1;
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/field/save`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        id: item.id,
        status: nextStatus,
      }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Failed to update field status");
    toast.success(nextStatus === 1 ? "Field activated" : "Field deactivated");
    invalidateSupportFieldsCache(appId);
    loadData();
  };

  const remove = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This field will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
    });
    if (!confirm.isConfirmed) return;
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/field/delete`, {
      method: "POST",
      body: JSON.stringify({ app_id: appId, id }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Delete failed");
    toast.success("Field deleted successfully");
    invalidateSupportFieldsCache(appId);
    loadData();
  };


  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("application/support/fields", {
        app_id: appId,
        field_id: (filters.field_id || "").trim(),
        name: (filters.name || "").trim(),
        field_type: (filters.field_type || "").trim(),
        status: filters.status,
      }, "support-fields.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner ns-support-list">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Support Fields</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => loadData()}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("support.fields.add") ? (
              <button
                type="button"
                className="add_app_btn btn_theme_primary"
                onClick={() => {
                  setForm({ id: null, field_id: "", name: "", field_type: "text", options_list: [], is_required: 0, status: 1 });
                  setIsViewMode(false);
                  setShowModal(true);
                }}
              >
                <MdAddCircle />
                <span>Add Field</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="list-page-filter-card">
          <button type="button" className="list-page-filter-accordion-header" onClick={() => setFilterAccordionOpen((o) => !o)} aria-expanded={filterAccordionOpen}>
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`} size={22} />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="list-page-filter-bar">
                <div className="list-page-filter-fields row g-3 align-items-end">
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input className="form-control" placeholder="Field ID" value={draftFilters.field_id} onChange={(e) => setDraftFilters((p) => ({ ...p, field_id: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input className="form-control" placeholder="Name" value={draftFilters.name} onChange={(e) => setDraftFilters((p) => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Type</label>
                    <input className="form-control" placeholder="Type" value={draftFilters.field_type} onChange={(e) => setDraftFilters((p) => ({ ...p, field_type: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <Select
                      inputId="support-fields-filter-status"
                      instanceId="support-fields-filter-status"
                      classNamePrefix="ns-support-fields-filter-status"
                      isSearchable
                      isClearable
                      placeholder="All Status"
                      options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => String(o.value) === String(draftFilters.status)) || null}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, status: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-12">
                    <div className="add_app">
                      <button type="button" className="add_app_btn btn_theme_primary" onClick={onApplyFilters}><span>Search</span></button>
                      <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onResetFilters}><span>Reset</span></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableScrollRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th className="col-action" style={{ textAlign: "right" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? <tr><td colSpan="4" className="text-center">Loading...</td></tr> : rows.length === 0 ? <tr><td colSpan="4" className="text-center">No fields found</td></tr> : rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          {(() => {
                            const c = nsAvatarColor(r.field_id || r.name);
                            return (
                              <div className="ns-requester">
                                <span className="ns-avatar" style={{ background: c.bg, color: c.text }}>
                                  {getAvatarInitials(r.name)}
                                </span>
                                <div style={{ minWidth: 0 }}>
                                  <div className="ns-requester-name">{r.name || "-"}</div>
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td>{r.field_type ? <span className="ns-pill">{r.field_type}</span> : "-"}</td>
                        <td>
                          {(() => {
                            const st = getStatusDisplay(r.status);
                            return <span className={st.badgeClass}>{st.label}</span>;
                          })()}
                        </td>
                        <td className="col-action" style={{ textAlign: "right" }}>
                          <div className="support-actions-menu" style={{ position: "relative", display: "inline-block" }}>
                            <button
                              type="button"
                              className="dropdown-trigger-btn btn btn-link p-0"
                              aria-label="Actions"
                              aria-expanded={openActionId === r.id}
                              onClick={(e) => handleOpenDropdown(e, r.id, r)}
                            >
                              <BsThreeDotsVertical />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPaginationBar
                page={page}
                setPage={(n) => writeParams(n, filters)}
                total={meta.total || 0}
                pageSize={pageSize}
                totalPages={meta.totalPages || 1}
                ariaLabel="Support fields pagination"
                loading={loading}
              />
            </div>
          </div>
        </div>

        {showModal ? (
          <div className="modal d-block ns-modal" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.35)" }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{form.id ? (isViewMode ? "View Field" : "Edit Field") : "Add Field"}</h5>
                  <button type="button" className="btn-close ms-auto" aria-label="Close" onClick={() => setShowModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-2"><label className="form-label">Name</label><input className="form-control" value={form.name} disabled={isViewMode} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></div>
                  <div className="mb-2">
                    <label className="form-label">Type</label>
                    <Select
                      inputId="support-fields-modal-type"
                      instanceId="support-fields-modal-type"
                      classNamePrefix="ns-support-fields-modal-type"
                      isSearchable
                      isClearable={false}
                      options={(fieldTypeOptions || []).map((type) => ({ value: type.value, label: type.label }))}
                      value={(fieldTypeOptions || []).map((type) => ({ value: type.value, label: type.label })).find((o) => o.value === form.field_type) || null}
                      onChange={(opt) =>
                        setForm((p) => {
                          const nextType = opt?.value || "text";
                          const keepOptions = FIELD_TYPES_WITH_OPTIONS.has(String(nextType || "").toLowerCase());
                          return { ...p, field_type: nextType, options_list: keepOptions ? p.options_list : [] };
                        })
                      }
                      isDisabled={isViewMode}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  {FIELD_TYPES_WITH_OPTIONS.has(String(form.field_type || "").trim().toLowerCase()) ? (
                    <div className="mb-2">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="form-label mb-0">Options</label>
                        {!isViewMode ? <button
                          type="button"
                          className="btn btn-sm btn-light"
                          onClick={() =>
                            setForm((p) => ({
                              ...p,
                              options_list: [...(p.options_list || []), { label: "", value: "" }],
                            }))
                          }
                        >
                          + Add Option
                        </button> : null}
                      </div>
                      {Array.isArray(form.options_list) && form.options_list.length ? (
                        form.options_list.map((item, idx) => (
                          <div className="d-flex gap-2 mb-2 form-options-list" key={`${idx}`}>
                            <input
                              className="form-control"
                              placeholder="Option Label"
                              value={item.label}
                              onChange={(e) =>
                                setForm((p) => {
                                  const next = [...(p.options_list || [])];
                                  next[idx] = { ...next[idx], label: e.target.value };
                                  return { ...p, options_list: next };
                                })
                              }
                              disabled={isViewMode}
                            />
                            <input
                              className="form-control"
                              placeholder="Option Value"
                              value={item.value}
                              onChange={(e) =>
                                setForm((p) => {
                                  const next = [...(p.options_list || [])];
                                  next[idx] = { ...next[idx], value: e.target.value };
                                  return { ...p, options_list: next };
                                })
                              }
                              disabled={isViewMode}
                            />
                            {!isViewMode ? <button
                              type="button"
                              className="btn btn-outline-danger"
                              aria-label="Remove option"
                              style={{ minWidth: 38, padding: "0 10px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                              onClick={() =>
                                setForm((p) => ({
                                  ...p,
                                  options_list: (p.options_list || []).filter((_, i) => i !== idx),
                                }))
                              }
                            >
                              <MdClose />
                            </button> : null}
                          </div>
                        ))
                      ) : (
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          No options added. Use this for dropdown or multi-select field types.
                        </div>
                      )}
                    </div>
                  ) : null}
                  <div className="mb-2">
                    <label className="form-label">Status</label>
                    <Select
                      inputId="support-fields-modal-status"
                      instanceId="support-fields-modal-status"
                      classNamePrefix="ns-support-fields-modal-status"
                      isSearchable={false}
                      isClearable={false}
                      options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => o.value === Number(form.status)) || DEFAULT_STATUS_OPTIONS[0]}
                      onChange={(opt) => setForm((p) => ({ ...p, status: Number(opt?.value ?? 1) }))}
                      isDisabled={isViewMode}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  {!isViewMode && can(form.id ? "support.fields.edit" : "support.fields.add") ? <button type="button" className="add_app_btn btn_theme_primary" onClick={save}><span>Save</span></button> : null}
                  <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={() => setShowModal(false)}><span>Close</span></button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {openActionItem && dropdownPosition
        ? createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPosition.left,
              top: dropdownPosition.top,
              zIndex: 999999,
              minWidth: "170px",
            }}
          >
            <div className="dropdown-menu-subscription">
              <button
                type="button"
                onClick={() => handleDropdownAction(async () => {
                  const id = openActionItem.field_id;
                  if (!id) return toast.error("Field ID not available");
                  await copyToClipboard(id);
                  toast.success("Field ID copied");
                })}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdContentCopy size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Copy Field ID</span>
              </button>
              {isStandardField(openActionItem) ? (
                <>
                  <button
                    type="button"
                    onClick={() => handleDropdownAction(() => openFieldInModal(openActionItem, { viewOnly: true }))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <MdEdit size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>View Field</span>
                  </button>
                  {can("support.fields.change_status") ? (
                  <button
                    type="button"
                    onClick={() => handleDropdownAction(() => toggleFieldStatus(openActionItem))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    {Number(openActionItem.status) === 1 ? (
                      <MdOutlineRemoveCircleOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    ) : (
                      <MdOutlineCheckCircle size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    )}
                    <span style={{ fontSize: "0.875rem" }}>{Number(openActionItem.status) === 1 ? "Deactivate" : "Activate"}</span>
                  </button>
                  ) : null}
                </>
              ) : (
                <>
                  {can("support.fields.edit") ? (
                  <button
                    type="button"
                    onClick={() => handleDropdownAction(() => openFieldInModal(openActionItem, { viewOnly: false }))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <MdEdit size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>Edit Field</span>
                  </button>
                  ) : null}
                  {can("support.fields.change_status") ? (
                  <button
                    type="button"
                    onClick={() => handleDropdownAction(() => toggleFieldStatus(openActionItem))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    {Number(openActionItem.status) === 1 ? (
                      <MdOutlineRemoveCircleOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    ) : (
                      <MdOutlineCheckCircle size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    )}
                    <span style={{ fontSize: "0.875rem" }}>{Number(openActionItem.status) === 1 ? "Deactivate" : "Activate"}</span>
                  </button>
                  ) : null}
                  {can("support.fields.delete") ? (
                  <button
                    type="button"
                    className="delete-class"
                    onClick={() => handleDropdownAction(() => remove(openActionItem.id))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <MdDeleteOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>Delete Field</span>
                  </button>
                  ) : null}
                </>
              )}
            </div>
          </div>,
          document.body
        )
        : null}
    </div>
  );
};

export default AppSupportFields;
